import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withCtx as _withCtx, vShow as _vShow, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { id: "speed-control" }
const _hoisted_2 = { id: "speed-buttons" }
const _hoisted_3 = {
  key: 5,
  id: "inline-speed-control"
}
const _hoisted_4 = {
  key: 0,
  id: "speed-text"
}

import { ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';

import { supportsTouchscreen } from "@cosmicds/vue-toolkit";
import { engineStore } from "@wwtelescope/engine-pinia";

import { usePlaybackControl } from '../wwt_playback_control';

interface Props {
  store: ReturnType<typeof engineStore>;
  color: string;
  maxSpeed?: number;
  defaultRate?: number;
  useInline?: boolean;
  showText?: boolean;
  rateDelta?: number;
  useOldControl?: boolean;
}

const minSpeed = 1;


export default /*@__PURE__*/_defineComponent({
  __name: 'SpeedControl',
  props: /*@__PURE__*/_mergeModels({
    store: {},
    color: { default: 'white' },
    maxSpeed: { default: 10000 },
    defaultRate: { default: 1 },
    useInline: { type: Boolean, default: false },
    showText: { type: Boolean, default: false },
    rateDelta: { default: 10 },
    useOldControl: { type: Boolean, default: false }
  }, {
    "playing": { type: Boolean, ...{default: false, required: true} },
    "playingModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["reset", "update:reverse", "update:playing", "slow-down", "speed-up", "set-rate"], ["update:playing"]),
  setup(__props: any, { emit: __emit }) {

const { 
  color, 
  maxSpeed, 
  defaultRate, 
  store, 
  showText, 
  rateDelta, 
  useInline,
  useOldControl,
} = __props;


const playing = _useModel<boolean>(__props, 'playing');
const emit = __emit;

const playbackVisible = ref(false);
const forceRate = ref(false);

const { timePlaying, clockRate, setSpeed } = usePlaybackControl(store, false);
setSpeed(defaultRate);

timePlaying.value = playing.value;
watch(playing, (v) => {timePlaying.value = v;});
watch(timePlaying, (v) => {playing.value = v;});

function clamp(val: number) {
  return Math.min(Math.max(val, minSpeed), maxSpeed);
}

const playbackRate = computed({
  get: function(): number {
    if (forceRate.value) {
      const sign = Math.sign(clockRate.value);
      return sign * Math.min(10, sign * clockRate.value);
    }
    return clockRate.value;
  },
  set: function(value: number) {
    clockRate.value = Math.sign(value) * clamp(Math.abs(value));
  }
});

function reverseRate() {
  playbackRate.value = -playbackRate.value;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function setDirection(direction: 'forward' | 'reverse' | null = null) {
  playbackRate.value = direction == 'reverse' ? -Math.abs(playbackRate.value) : Math.abs(playbackRate.value);
}

function increaseRate() {
  const sign = Math.sign(playbackRate.value);
  const abs = Math.abs(playbackRate.value);
  const newRate = abs * rateDelta;
  playbackRate.value = sign * clamp(newRate);
}
function decreaseRate() {
  const sign = Math.sign(playbackRate.value);
  const abs = Math.abs(playbackRate.value);
  const newRate = abs / rateDelta;
  playbackRate.value = sign * clamp(newRate);
}


// either moves to reverse time (at -1x) or slows down by rateDelta
function reverseOrIncreaseReversePlaybackRate() {
  forceRate.value = false;
  const sign = Math.sign(playbackRate.value);
  if (sign > 0) {
    playbackRate.value = -Math.min(playbackRate.value, 1);
    return;
  }
  const abs = Math.abs(playbackRate.value);
  let ezrate = Math.floor(Math.log10(abs));
  ezrate -= sign * Math.log10(rateDelta);
  playbackRate.value = sign * clamp(Math.pow(10, Math.abs(ezrate)));
}

// etiher switches to forward time (at 1x) or speeds up by rateDelta
function forwardOrIncreaseForwardPlaybackRate() {
  forceRate.value = false;
  const sign = Math.sign(playbackRate.value);
  if (sign < 0 ) {
    playbackRate.value = -Math.max(playbackRate.value,-1);
    return;
  }
  const abs = Math.abs(playbackRate.value);
  let ezrate = Math.floor(Math.log10(abs));
  ezrate += sign * Math.log10(rateDelta);
  playbackRate.value = sign * clamp(Math.pow(10, Math.abs(ezrate)));
}

const { smAndDown } = useDisplay();
const mobile = computed(() => smAndDown && supportsTouchscreen());

const allowClickOutside = ref(false);
function onClickOutside() {
  // console.log('onclickoutside', playbackVisible.value, allowClickOutside.value);
  if (playbackVisible.value && allowClickOutside.value) {
    playbackVisible.value = false;
    return;
  }
  allowClickOutside.value = playbackVisible.value;
}

return (_ctx: any,_cache: any) => {
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_playback_control = _resolveComponent("playback-control")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      useOldControl
        ? (_openBlock(), _createBlock(_component_icon_button, {
            key: 0,
            id: "reverse-speed",
            "fa-icon": 'angles-left',
            onActivate: _cache[0] || (_cache[0] = 
          () => {
            reverseOrIncreaseReversePlaybackRate();
            timePlaying.value = true;
          }
        ),
            color: color,
            "focus-color": color,
            "tooltip-text": playbackRate.value < 0 ? 'Reverse Faster' : 'Reverse',
            "tooltip-location": "top",
            "tooltip-offset": "5px",
            faSize: "1x",
            "show-tooltip": !mobile.value
          }, null, 8, ["tooltip-text", "show-tooltip"]))
        : _createCommentVNode("", true),
      useOldControl
        ? (_openBlock(), _createBlock(_component_icon_button, {
            key: 1,
            id: "forward-speed",
            "fa-icon": 'angles-right',
            onActivate: _cache[1] || (_cache[1] = 
          () => {
            if (!_unref(timePlaying)) {
              timePlaying.value = true;
            } else {
              forwardOrIncreaseForwardPlaybackRate();
            }
            emit('speed-up', playbackRate.value);
          }
        ),
            color: color,
            "focus-color": color,
            "tooltip-text": playbackRate.value > 0 ? 'Faster' : 'Forward',
            "tooltip-location": "top",
            "tooltip-offset": "5px",
            faSize: "1x",
            "show-tooltip": !mobile.value
          }, null, 8, ["tooltip-text", "show-tooltip"]))
        : _createCommentVNode("", true),
      _createVNode(_component_icon_button, {
        onActivate: _cache[2] || (_cache[2] = () => {
          reverseRate();
          emit('update:reverse', playbackRate.value < 0);
        }),
        "md-icon": playbackRate.value < 0 ? 'mdi-step-forward-2' : 'mdi-step-backward-2',
        color: color,
        "focus-color": color,
        "tooltip-text": playbackRate.value < 0 ? 'Play time forward' : 'Play time backwards',
        "tooltip-location": "top",
        "tooltip-offset": "5px",
        "show-tooltip": !mobile.value,
        "md-size": "18"
      }, null, 8, ["md-icon", "tooltip-text", "show-tooltip"]),
      _createVNode(_component_icon_button, {
        id: "play-pause-icon",
        "fa-icon": !_unref(timePlaying) ? 'play' : 'pause',
        onActivate: _cache[3] || (_cache[3] = 
          () => {
            timePlaying.value = !_unref(timePlaying);
            emit('update:playing', _unref(timePlaying));
          }
        ),
        color: color,
        "focus-color": color,
        "tooltip-text": "Play/Pause",
        "tooltip-location": "top",
        "tooltip-offset": "5px",
        faSize: "1x",
        "show-tooltip": !mobile.value
      }, null, 8, ["fa-icon", "show-tooltip"]),
      (!useOldControl)
        ? (_openBlock(), _createBlock(_component_icon_button, {
            key: 2,
            id: "slow-down",
            "fa-icon": 'angles-down',
            onActivate: _cache[4] || (_cache[4] = 
          () => {
            decreaseRate();
            timePlaying.value = true;
            emit('slow-down', playbackRate.value);
          }
        ),
            color: color,
            "focus-color": color,
            "tooltip-text": `Slow down ${rateDelta}x`,
            "tooltip-location": "top",
            "tooltip-offset": "5px",
            faSize: "1x",
            "show-tooltip": !mobile.value
          }, null, 8, ["tooltip-text", "show-tooltip"]))
        : _createCommentVNode("", true),
      (!useOldControl)
        ? (_openBlock(), _createBlock(_component_icon_button, {
            key: 3,
            id: "speed-up",
            "fa-icon": 'angles-up',
            onActivate: _cache[5] || (_cache[5] = 
          () => {
            increaseRate();
            timePlaying.value = true;
            emit('speed-up', playbackRate.value);
          }
        ),
            color: color,
            "focus-color": color,
            "tooltip-text": `Speed up ${rateDelta}x`,
            "tooltip-location": "top",
            "tooltip-offset": "5px",
            faSize: "1x",
            "show-tooltip": !mobile.value
          }, null, 8, ["tooltip-text", "show-tooltip"]))
        : _createCommentVNode("", true),
      (!useInline)
        ? (_openBlock(), _createBlock(_component_v_dialog, {
            key: 4,
            modelValue: playbackVisible.value,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((playbackVisible).value = $event)),
            scrim: false,
            location: "top",
            offset: "40",
            "location-strategy": "connected",
            persistent: "",
            "no-click-animation": "",
            "retain-focus": false
          }, {
            activator: _withCtx(({ props }) => [
              _createVNode(_component_icon_button, _mergeProps({
                id: "speed-control-icon",
                onActivate: _cache[6] || (_cache[6] = 
              () => {
                playbackVisible.value = !playbackVisible.value;
              }
            ),
                "fa-icon": playbackVisible.value ? 'times' : 'gauge-high',
                color: color,
                "focus-color": color,
                "tooltip-text": "More Speed Controls",
                "tooltip-location": "top",
                "tooltip-offset": "5px",
                faSize: "1x",
                "show-tooltip": !mobile.value
              }, props), null, 16, ["fa-icon", "show-tooltip"])
            ]),
            default: _withCtx(() => [
              (playbackVisible.value)
                ? _withDirectives((_openBlock(), _createBlock(_component_playback_control, {
                    key: 0,
                    class: "desktop-playback-control",
                    "model-value": playbackRate.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = 
            (value) => {
              forceRate.value = false;
              playbackRate.value = value;
              emit('set-rate', value);
            }
          ),
                    paused: !_unref(timePlaying),
                    onPaused: _cache[8] || (_cache[8] = (paused) => {
            timePlaying.value = !paused;
            emit('update:playing', !paused);
          }),
                    "max-power": Math.log10(maxSpeed),
                    max: Math.log10(maxSpeed) + 1,
                    color: color,
                    inline: false,
                    "show-close-button": "",
                    onClose: _cache[9] || (_cache[9] = 
            () => {
              playbackVisible.value = false;
            }
          )
                  }, null, 8, ["model-value", "paused", "max-power", "max"])), [
                    [_directive_click_outside, onClickOutside]
                  ])
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["modelValue"]))
        : _createCommentVNode("", true),
      useInline
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_icon_button, {
              id: "speed-control-icon",
              onActivate: _cache[11] || (_cache[11] = 
            () => {
              playbackVisible.value = !playbackVisible.value;
              allowClickOutside.value = false; // prevent onClickOutside from hiding it.
            }
          ),
              "fa-icon": playbackVisible.value ? 'times' : 'gauge-high',
              color: color,
              "focus-color": color,
              "tooltip-text": "Time Controls",
              "tooltip-location": "top",
              "tooltip-offset": "5px",
              faSize: "1x",
              "show-tooltip": !mobile.value
            }, null, 8, ["fa-icon", "show-tooltip"]),
            _withDirectives(_createVNode(_component_playback_control, {
              class: "mobile-playback-control",
              "model-value": playbackRate.value,
              "onUpdate:modelValue": _cache[12] || (_cache[12] = 
            (value) => {
              forceRate.value = false;
              playbackRate.value = value;
              emit('set-rate', value);
            }
          ),
              paused: !_unref(timePlaying),
              onPaused: _cache[13] || (_cache[13] = ($event: any) => (timePlaying.value = !$event)),
              "max-power": Math.log10(maxSpeed),
              max: Math.log10(maxSpeed) + 1,
              color: color,
              inline: true,
              "inline-button": "",
              onClose: _cache[14] || (_cache[14] = 
            () => {
              playbackVisible.value = false;
            }
          )
            }, null, 8, ["model-value", "paused", "max-power", "max"]), [
              [_vShow, playbackVisible.value],
              [_directive_click_outside, onClickOutside]
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(_component_icon_button, {
        id: "reset",
        "fa-icon": 'home',
        onActivate: _cache[15] || (_cache[15] = 
          () => {
            playbackRate.value = 100;
            timePlaying.value = false;
            forceRate.value = false;
            emit('reset');
          }
        ),
        color: color,
        "focus-color": color,
        "tooltip-text": "Reset",
        "tooltip-location": "top",
        "tooltip-offset": "5px",
        faSize: "1x",
        "show-tooltip": !mobile.value
      }, null, 8, ["show-tooltip"])
    ]),
    showText
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(Math.round(playbackRate.value)) + "x " + _toDisplayString(_unref(timePlaying) ? '' : '(Paused)'), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})